import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../utils/constants";
import { getAuthToken } from "../../utils/authToken";

export const getOrders = createAsyncThunk(
  "orders/getOrders",
  async (_, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);

      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }

      const res = await axios.get(`${BASE_URL}/orders`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error('Помилка при отриманні замовлень:', err);
      const msg = err.response?.data?.message || err.message || 'Помилка завантаження замовлень';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const getUserOrders = createAsyncThunk(
  "orders/getUserOrders",
  async (_, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);

      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть в акаунт');
      }

      const res = await axios.get(`${BASE_URL}/orders/user`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return res.data;
    } catch (err) {
      console.log('Помилка при отриманні замовлень користувача:', err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateOrder = createAsyncThunk(
  "orders/updateOrder",
  async ({ id, orderData, cart }, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);

      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }

      const res = await axios.put(`${BASE_URL}/orders/${id}`, {
        orderData,
        cart
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return res.data;
    } catch (err) {
      console.log('Помилка при оновленні замовлення:', err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ id, status, trackingNumber, isPaid, deliveryComment, adminNote }, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }
      const res = await axios.post(`${BASE_URL}/orders/${id}/status`, {
        status,
        trackingNumber,
        isPaid,
        deliveryComment,
        adminNote,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "orders/deleteOrder",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);

      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }

      await axios.delete(`${BASE_URL}/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return id;
    } catch (err) {
      console.log('Помилка при видаленні замовлення:', err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    list: [],
    userOrders: [],
    isLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getUserOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userOrders = action.payload;
      })
      .addCase(getUserOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.list.findIndex(order => order.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = state.list.filter(order => order.id !== action.payload);
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const index = state.list.findIndex((order) => order.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      });
  },
});

export default ordersSlice.reducer;

